import { useEffect, useState } from "react";
import type { PrecompiledWeek } from "../types/Entities";

// Resolves a library (precompiled) progression's full snapshot — label,
// updatedAt (version marker) and weeks — from its id. Used by the
// collega/scollega feature to (a) detect that the library has a newer version
// than the embedded snapshot and (b) pull the latest weeks on "Aggiorna".
//
// Mirrors usePrecompiledProgressionName's dynamic-import + module cache pattern
// so pure-function component tests stay transform-free.
export interface PrecompiledProgressionMeta {
  label: string | null;
  updatedAt: string | null;
  weeks: PrecompiledWeek[] | null;
}

const cache = new Map<string, PrecompiledProgressionMeta>();

export function usePrecompiledProgressionMeta(
  id: string | null | undefined
): PrecompiledProgressionMeta | null {
  const [meta, setMeta] = useState<PrecompiledProgressionMeta | null>(
    id ? cache.get(id) ?? null : null
  );

  useEffect(() => {
    if (!id) {
      setMeta(null);
      return;
    }
    const cached = cache.get(id);
    if (cached) {
      setMeta(cached);
      return;
    }
    let active = true;
    import("../api/Entities")
      .then(({ Entities }) => {
        if (typeof Entities?.getPrecompiledProgression !== "function") {
          if (active) setMeta(null);
          return;
        }
        return Entities.getPrecompiledProgression(id).then(({ data }) => {
          const p: any = data.progression;
          const resolved: PrecompiledProgressionMeta = {
            label: p?.label ?? null,
            updatedAt: p?.updatedAt ?? null,
            weeks: (p?.weeks as PrecompiledWeek[]) ?? null,
          };
          cache.set(id, resolved);
          if (active) setMeta(resolved);
        });
      })
      .catch(() => {
        if (active) setMeta(null);
      });
    return () => {
      active = false;
    };
  }, [id]);

  return meta;
}
